import { create } from 'zustand';

import { ApiError, apiFetch, refreshAccessToken } from '../api/client';
import { clearAccessToken, getAccessToken, setAccessToken } from '../api/tokenStore';

export interface OwnerUser {
  id: string;
  full_name: string;
  email: string;
  role: string;
  business_id: string | null;
  business_name: string | null;
  has_business: boolean;
}

interface AuthState {
  user: OwnerUser | null;
  status: 'idle' | 'checking' | 'authenticated' | 'unauthenticated';
  error: string | null;
  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

let refreshTimer: ReturnType<typeof setInterval> | null = null;
const REFRESH_INTERVAL_MS = 12 * 60 * 1000;

function startRefreshTimer() {
  stopRefreshTimer();
  refreshTimer = setInterval(async () => {
    if (getAccessToken()) {
      await refreshAccessToken();
    }
  }, REFRESH_INTERVAL_MS);
}

function stopRefreshTimer() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

async function revokeOwnerSession() {
  try {
    await apiFetch('/auth/logout?portal=owner', { method: 'POST', skipRefresh: true });
  } catch {
    // best-effort
  }
  clearAccessToken();
}

function ownerLoginError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return typeof err.body.detail === 'string'
        ? err.body.detail
        : 'This account cannot access the Owner Portal.';
    }
  }
  return 'Invalid email or password.';
}

async function loadOwnerProfile(): Promise<OwnerUser> {
  const user = await apiFetch<OwnerUser>('/owner/me');
  if (user.role !== 'business_owner') {
    throw new ApiError(403, { detail: 'Platform admin accounts must use the Admin Panel (localhost:5174).' });
  }
  if (!user.has_business) {
    throw new ApiError(
      403,
      { detail: 'No business is linked to this owner account. Ask your platform admin to link your login.' },
    );
  }
  return user;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: 'idle',
  error: null,

  init: async () => {
    set({ status: 'checking' });
    try {
      if (!getAccessToken()) {
        const token = await refreshAccessToken();
        if (!token) {
          set({ status: 'unauthenticated', user: null });
          return;
        }
      }
      const user = await loadOwnerProfile();
      startRefreshTimer();
      set({ status: 'authenticated', user, error: null });
    } catch (err) {
      await revokeOwnerSession();
      set({
        status: 'unauthenticated',
        user: null,
        error: err instanceof ApiError && err.status === 403 ? ownerLoginError(err) : null,
      });
    }
  },

  login: async (email, password) => {
    set({ error: null });
    try {
      const response = await apiFetch<{ access_token: string }>('/auth/login', {
        method: 'POST',
        body: { email, password, portal: 'owner' },
        skipAuth: true,
        skipRefresh: true,
      });
      setAccessToken(response.access_token);
      const user = await loadOwnerProfile();
      startRefreshTimer();
      set({ status: 'authenticated', user, error: null });
    } catch (err) {
      await revokeOwnerSession();
      set({ status: 'unauthenticated', user: null, error: ownerLoginError(err) });
      throw new Error('login_failed');
    }
  },

  logout: async () => {
    stopRefreshTimer();
    await revokeOwnerSession();
    set({ status: 'unauthenticated', user: null, error: null });
  },
}));
